import { resolveAssetUrl, type GalleryItem } from '../api/client';

export interface GalleryCardProps {
  item: GalleryItem;
  /** When provided, render a delete button that calls this with the item id. */
  onDelete?: (id: string) => void;
}

export function GalleryCard(props: GalleryCardProps) {
  const { item, onDelete } = props;
  return (
    <figure className="group relative overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm motion-safe:transition-shadow hover:shadow-md dark:border-neutral-800 dark:bg-neutral-900">
      <div className="aspect-[4/3] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        <img
          src={resolveAssetUrl(item.thumbnail_url || item.url)}
          alt={describeGalleryItem(item)}
          loading="lazy"
          className="h-full w-full object-cover motion-safe:transition-transform motion-safe:group-hover:scale-[1.02]"
        />
      </div>
      {onDelete ? (
        <button
          type="button"
          onClick={() => onDelete(item.id)}
          aria-label={`Delete ${item.title ?? 'uploaded artwork'}`}
          className="absolute right-2 top-2 inline-flex h-10 min-w-[44px] cursor-pointer items-center justify-center rounded-md bg-neutral-900/70 px-3 text-xs font-medium text-neutral-100 opacity-0 motion-safe:transition-opacity hover:bg-rose-700/90 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent group-hover:opacity-100"
        >
          Delete
        </button>
      ) : null}
      <figcaption className="flex flex-col gap-1 p-3">
        <span className="line-clamp-1 text-sm font-semibold text-neutral-900 dark:text-neutral-50">
          {item.title ?? 'Untitled artwork'}
        </span>
        {item.notes ? (
          <span className="line-clamp-2 text-xs text-neutral-600 dark:text-neutral-400">
            {item.notes}
          </span>
        ) : null}
        <time
          dateTime={item.created_at}
          className="text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400"
        >
          {new Date(item.created_at).toLocaleDateString()}
        </time>
      </figcaption>
    </figure>
  );
}

function describeGalleryItem(item: GalleryItem): string {
  const parts: string[] = ['Uploaded artwork'];
  if (item.title) parts.push(`titled "${item.title}"`);
  if (item.notes) parts.push(`notes: ${item.notes}`);
  return parts.join(', ');
}
